import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner, Input } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Select from "react-select";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  getInvoiceVendors,
  createInvoiceVendor,
  updateInvoiceVendor,
  deleteInvoiceVendor,
  getInvoiceCategories,
  uploadInvoiceVendorLogo,
  removeInvoiceVendorLogo,
} from "./Api";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const LogoThumb = ({ src, size = 34 }) => (
  src ? (
    <img
      src={src} alt=""
      style={{ width: size, height: size, objectFit: "contain", borderRadius: 6, border: "1px solid #E2E8F0", background: "#fff" }}
    />
  ) : (
    <span style={{
      width: size, height: size, borderRadius: 6, border: "1px dashed #CBD5E1",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      <FeatherIcon icon="image" size={14} color="#CBD5E1" />
    </span>
  )
);

// Pick / replace / clear the optional logo. Nothing uploads until the row is saved.
const LogoPicker = ({ preview, onPick, onClear }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <LogoThumb src={preview} />
    <label className="iv-btn iv-btn--ghost" style={{ margin: 0, cursor: "pointer", padding: "4px 8px" }}>
      <FeatherIcon icon="upload" size={12} /> {preview ? "Change" : "Upload"}
      <input
        type="file" accept={LOGO_TYPES.join(",")} style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPick(f); }}
      />
    </label>
    {preview && (
      <FeatherIcon icon="x" size={13} color="#DC2626" style={{ cursor: "pointer" }} aria-label="Remove logo" onClick={onClear} />
    )}
  </div>
);

const FInput = ({ value, onChange, placeholder, type = "text" }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ borderColor: "#E2E8F0", borderRadius: 7, fontSize: "0.82rem" }}
  />
);

const InlineVendorRow = ({ data, isNew, categories, onSave, onCancel }) => {
  const [row, setRow] = useState({
    isActive: true, email: "", phone: "", companyName: "", gstNumber: "", categoryIds: [], ...data,
  });
  // logoFile: a newly picked file; logoRemoved: clear the saved logo on save.
  const [logoFile, setLogoFile] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoPreview, setLogoPreview] = useState(data.logoUrl || null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  useEffect(() => () => { if (logoFile) URL.revokeObjectURL(logoPreview); }, [logoFile, logoPreview]);

  const pickLogo = (file) => {
    if (!LOGO_TYPES.includes(file.type)) { toast.error("Logo must be a PNG, JPG or WebP image"); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error("Logo must be 2 MB or smaller"); return; }
    setLogoFile(file);
    setLogoRemoved(false);
    setLogoPreview(URL.createObjectURL(file));
  };
  const clearLogo = () => {
    setLogoFile(null);
    setLogoRemoved(!!data.logoUrl);
    setLogoPreview(null);
  };

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error("Vendor name is required"); return; }
    if (!row.companyName?.trim()) { toast.error("Company name is required"); return; }
    if (!row.categoryIds || row.categoryIds.length === 0) {
      toast.error("Please choose at least one category");
      return;
    }
    setSaving(true);
    try { await onSave(row, { logoFile, logoRemoved }); } finally { setSaving(false); }
  };

  return (
    <tr style={{ background: isNew ? "#ECFDF5" : "#EEF2FF" }}>
      <td style={{ width: 40, textAlign: "center", padding: "8px 10px" }}>
        <FeatherIcon icon={isNew ? "plus" : "edit-2"} size={12} color={isNew ? "#059669" : "#4F46E5"} />
      </td>
      <td style={{ minWidth: 170, padding: "8px 10px" }}>
        <FInput value={row.name} onChange={set("name")} placeholder="Vendor name…" />
      </td>
      <td style={{ minWidth: 180, padding: "8px 10px" }}>
        <FInput value={row.companyName} onChange={set("companyName")} placeholder="Company name (required)" />
      </td>
      <td style={{ minWidth: 220, padding: "8px 10px" }}>
        <Select
          isMulti
          options={categories.map((c) => ({ value: c._id, label: c.name }))}
          value={(row.categoryIds || [])
            .map((id) => {
              const c = categories.find((x) => x._id === id);
              return c ? { value: c._id, label: c.name } : null;
            })
            .filter(Boolean)}
          onChange={(opts) => set("categoryIds")((opts || []).map((o) => o.value))}
          placeholder="Choose categories…"
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          classNamePrefix="select"
        />
      </td>
      <td style={{ minWidth: 190, padding: "8px 10px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <FInput value={row.email} onChange={set("email")} placeholder="vendor@email.com" type="email" />
          <FInput value={row.phone} onChange={set("phone")} placeholder="Phone (optional)" />
        </div>
      </td>
      <td style={{ minWidth: 140, padding: "8px 10px" }}>
        <FInput value={row.gstNumber} onChange={set("gstNumber")} placeholder="GST No. (optional)" />
      </td>
      <td style={{ minWidth: 150, padding: "8px 10px" }}>
        <LogoPicker preview={logoPreview} onPick={pickLogo} onClear={clearLogo} />
      </td>
      <td style={{ width: 100, textAlign: "center", padding: "8px 10px" }}>
        {isNew ? (
          <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>Active</span>
        ) : (
          <select
            value={row.isActive ? "true" : "false"}
            onChange={(e) => set("isActive")(e.target.value === "true")}
            style={{
              border: "1px solid #E2E8F0", borderRadius: 7, fontSize: "0.78rem",
              padding: "4px 8px", cursor: "pointer", outline: "none",
              background: row.isActive ? "#ECFDF5" : "#FEF2F2",
              color: row.isActive ? "#059669" : "#DC2626",
              fontWeight: 700, width: "100%",
            }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </td>
      <td style={{ width: 130, textAlign: "center", padding: "8px 10px" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? "Add" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InvoiceVendorsTable = ({ userRole, canManage, onRefresh }) => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Company accounts and lead technical staff may manage vendors.
  const isCompany = canManage ?? ((userRole || "").toLowerCase() === "company");

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getInvoiceCategories({ activeOnly: "true" });
      setCategories(res?.data || []);
    } catch { /* handled */ }
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceVendors(showInactive ? {} : { activeOnly: "true" });
      setVendors(res?.data || []);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [showInactive]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  // The vendor is saved first; the logo needs its id. A failed logo upload
  // keeps the saved vendor (the error toast says what went wrong).
  const applyLogo = async (id, { logoFile, logoRemoved }) => {
    try {
      if (logoFile) await uploadInvoiceVendorLogo(id, logoFile);
      else if (logoRemoved) await removeInvoiceVendorLogo(id);
    } catch { /* toast handled */ }
  };

  const brandingPayload = (row) => ({
    name: row.name.trim(),
    companyName: row.companyName.trim(),
    gstNumber: row.gstNumber?.trim() || "",
    categoryIds: row.categoryIds || [],
    email: row.email?.trim() || "",
    phone: row.phone?.trim() || "",
  });

  const handleCreate = async (row, logo) => {
    const res = await createInvoiceVendor(brandingPayload(row));
    if (res?.data?._id) await applyLogo(res.data._id, logo);
    setShowNewRow(false);
    fetchVendors();
    onRefresh?.();
  };

  const handleUpdate = async (id, row, logo) => {
    await updateInvoiceVendor(id, { ...brandingPayload(row), isActive: row.isActive });
    await applyLogo(id, logo);
    setEditingId(null);
    fetchVendors();
    onRefresh?.();
  };

  const handleDelete = async (v) => {
    const res = await Swal.fire({
      title: "Deactivate Vendor?",
      html: `<p>Deactivate <strong>${v.name}</strong>? It will no longer appear when creating invoices.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Deactivate",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try { await deleteInvoiceVendor(v._id); fetchVendors(); onRefresh?.(); } catch { /* handled */ }
  };

  const colSpan = isCompany ? 9 : 8;

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="briefcase" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Vendors</div>
          </div>
        </div>
        <div className="iv-header__actions" style={{ gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", fontWeight: 600, cursor: "pointer" }}>
            <Input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ marginTop: 0 }} />
            Show inactive
          </label>
          {isCompany && (
            <button type="button" className="iv-btn iv-btn--primary" onClick={() => setShowNewRow(true)} disabled={showNewRow || categories.length === 0}>
              <FeatherIcon icon="plus" size={14} /> New Vendor
            </button>
          )}
        </div>
      </div>

      {isCompany && categories.length === 0 && (
        <div style={{ padding: "10px 16px", background: "#FFFBEB", color: "#B45309", fontSize: "0.8rem", borderBottom: "1px solid #FDE68A" }}>
          Add at least one Category first — every vendor must belong to a category.
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table className="iv-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
              <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 10 }}>
                Company Name <span style={{ color: "#DC2626" }}>*</span>
              </th>
              <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Category</th>
              <th style={{ minWidth: 190, textAlign: "left", paddingLeft: 10 }}>Contact Info</th>
              <th style={{ minWidth: 140, textAlign: "left", paddingLeft: 10 }}>GST No.</th>
              <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Logo</th>
              <th style={{ width: 100 }}>Status</th>
              {isCompany && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineVendorRow
                isNew
                data={{ name: "", companyName: "", gstNumber: "", categoryId: "", email: "", phone: "" }}
                categories={categories}
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
            )}

            {!loading && vendors.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={colSpan} className="iv-empty-cell">
                  <div className="iv-empty" style={{ minHeight: "20vh" }}>
                    <div className="iv-empty__icon-wrap"><FeatherIcon icon="briefcase" size={22} color="#4F46E5" /></div>
                    <div className="iv-empty__title">No Vendors</div>
                    <div className="iv-empty__sub">{isCompany ? "Add your first vendor to start creating invoices." : "Ask a company admin to add vendors."}</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && vendors.map((v) => {
              if (editingId === v._id) {
                return (
                  <InlineVendorRow
                    key={v._id}
                    data={{
                      ...v,
                      categoryIds:
                        (v.categories && v.categories.map((c) => c._id)) ||
                        (v.categoryId ? [v.categoryId] : []),
                    }}
                    categories={categories}
                    onSave={(row, logo) => handleUpdate(v._id, row, logo)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr key={v._id} className="iv-row">
                  <td style={{ textAlign: "center", width: 40 }}><FeatherIcon icon="briefcase" size={13} color="#94A3B8" /></td>
                  <td style={{ textAlign: "left", paddingLeft: 10, fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{v.name}</td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#0F172A", fontSize: "0.82rem" }}>
                    {v.companyName || (
                      <span style={{ color: "#B45309", fontSize: "0.74rem", fontWeight: 600 }}>Missing — edit to add</span>
                    )}
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10 }}>
                    {(v.categories && v.categories.length
                      ? v.categories
                      : v.category
                        ? [v.category]
                        : []
                    ).map((c) => (
                      <span key={c._id} style={{ display: "inline-block", background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE", borderRadius: 6, padding: "2px 9px", fontSize: "0.74rem", fontWeight: 600, marginRight: 4, marginBottom: 4 }}>
                        {c.name}
                      </span>
                    ))}
                    {(!v.categories || v.categories.length === 0) && !v.category && "—"}
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>
                    {v.email || v.phone ? (
                      <>
                        {v.email && <div>{v.email}</div>}
                        {v.phone && <div style={{ color: "#64748B" }}>{v.phone}</div>}
                      </>
                    ) : <span style={{ color: "#CBD5E1" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10, color: "#475569", fontSize: "0.8rem" }}>
                    {v.gstNumber || <span style={{ color: "#CBD5E1" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10 }}>
                    {v.logoUrl ? <LogoThumb src={v.logoUrl} /> : <span style={{ color: "#CBD5E1" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{
                      background: v.isActive ? "#ECFDF5" : "#FEF2F2",
                      color: v.isActive ? "#059669" : "#DC2626",
                      border: v.isActive ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                      borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
                    }}>
                      {v.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isCompany && (
                    <td style={{ textAlign: "center" }}>
                      <div className="iv-action-group">
                        <button type="button" className="iv-ebtn" title="Edit" onClick={() => setEditingId(v._id)}>
                          <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                        </button>
                        {v.isActive && (
                          <button type="button" className="iv-dbtn" title="Deactivate" onClick={() => handleDelete(v)}>
                            <FeatherIcon icon="x-circle" size={13} color="#DC2626" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InvoiceVendorsTable };
